/**
 * Shared primitives for queue-style admin workspaces (moderation cases, support tickets).
 * Keep these generic: anything specific to one queue belongs in that feature's *Ui module.
 */

export const badgeBase = 'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold leading-4 whitespace-nowrap'

export const formatDateTime = (value?: string | null) => {
  if (!value) return '—'
  try {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    }).format(new Date(value))
  } catch {
    return value
  }
}

export const hoursSince = (value: string) => {
  const then = new Date(value).getTime()
  if (Number.isNaN(then)) return 0
  return Math.max(0, (Date.now() - then) / 36e5)
}

export const formatAge = (value: string) => {
  const hours = hoursSince(value)
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))}m`
  if (hours < 48) return `${Math.round(hours)}h`
  return `${Math.floor(hours / 24)}d`
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange
}: {
  options: Array<{ value: T; label: string }>
  value: T
  onChange: (value: T) => void
}) {
  return (
    <div className="flex gap-1 rounded-lg border border-border bg-muted/40 p-1 text-xs font-medium">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          className={`rounded-md px-3 py-1 transition ${
            value === option.value ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}

export function StatTile({
  label,
  value,
  icon,
  tone = 'default',
  active,
  onClick
}: {
  label: string
  value: number | string
  icon: React.ReactNode
  tone?: 'default' | 'danger' | 'indigo' | 'amber'
  active?: boolean
  onClick?: () => void
}) {
  const toneClasses =
    tone === 'danger' ? 'text-red-700' : tone === 'indigo' ? 'text-indigo-700' : tone === 'amber' ? 'text-amber-700' : 'text-foreground'
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex min-w-36 flex-1 items-center gap-3 rounded-xl border p-3 text-left transition ${
        active ? 'border-primary bg-primary/5' : 'border-border/60 bg-card hover:border-primary/30'
      }`}
    >
      <span className="text-muted-foreground">{icon}</span>
      <span>
        <span className={`block font-mono text-xl font-bold leading-6 ${toneClasses}`}>{value}</span>
        <span className="block text-xs text-muted-foreground">{label}</span>
      </span>
    </button>
  )
}

/** Age chip whose color escalates as an item sits unhandled. Pass muted for closed/resolved items. */
export function AgeIndicator({
  since,
  muted = false,
  warnAfterHours = 8,
  critAfterHours = 24
}: {
  since: string
  muted?: boolean
  warnAfterHours?: number
  critAfterHours?: number
}) {
  const hours = hoursSince(since)
  const classes = muted
    ? 'text-muted-foreground'
    : hours >= critAfterHours
      ? 'text-red-700'
      : hours >= warnAfterHours
        ? 'text-amber-700'
        : 'text-muted-foreground'
  const dot = muted ? 'bg-stone-300' : hours >= critAfterHours ? 'bg-red-500' : hours >= warnAfterHours ? 'bg-amber-500' : 'bg-emerald-500'
  return (
    <span className={`inline-flex items-center gap-1.5 font-mono text-xs ${classes}`} title={formatDateTime(since)}>
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
      {formatAge(since)}
    </span>
  )
}

/** Initials avatar used for assignees and users without profile images. */
export function InitialsAvatar({ name, imageUrl, size = 'h-8 w-8' }: { name?: string | null; imageUrl?: string | null; size?: string }) {
  if (imageUrl) {
    return <img src={imageUrl} alt={name ?? 'avatar'} className={`${size} rounded-full object-cover`} />
  }
  const initials = (name ?? '?')
    .split(/\s+/)
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase()
  return (
    <span className={`${size} inline-flex items-center justify-center rounded-full bg-primary/10 font-mono text-xs font-bold text-primary`}>
      {initials || '?'}
    </span>
  )
}
